import React, {useEffect, useState, useCallback} from 'react';
import {Container, Grid, Tab, Tabs, Typography} from "@mui/material";
import {
  transformBooking,
  BuyerBookingTabs
} from '../../components/bookingReports/buyer/data';
import {formatTabHeader} from '../../components/bookingReports/data';
import AdjustBookingDialog from '../../components/bookingReports/AdjustBookingDialog';
import {TabContext, TabPanel} from "@mui/lab";
import type {Account} from "../../types/Account";
import BookingTab from '../../components/bookingReports/BookingTab';
import BuyerBookingFilter from '../../components/bookingReports/buyer/BuyerBookingFilter';
import {withSnackbar} from '../../components/hocs/withSnackbar';
import {getErrorMessageForNonStandardAndStandardResponse} from '../../util/NetworkErrorUtil';
import { getBuyerBookings } from '../../components/bookedSpaces/request/booked-spaces-requests';
import {hasAdminAccess} from '../../util/AccountUtils';
import { useStyles } from '../view-styles/bookingStyles';
import type {Booking} from "../../types/Booking";
import useUrlQueryParams from "../../hooks/useUrlQueryParams";
import {parseCurrencyValue} from "../../util/PaymentUtils";
import { AccountType, BookingStatus, DateFormats } from '../../components/constants/securspace-constants';
import { BookingStatusesEligibleForPaymentMethodUpdate, BookingStatusesEligibleForCancellation } from '../../components/constants/securspace-constants';
import BuyerActiveDialogs from '../../components/bookingReports/buyer/dialogs/BuyerActiveDialogs';
import BuyerPendingDialogs from '../../components/bookingReports/buyer/dialogs/BuyerPendingDialogs';
import Busy from '../../components/Busy';
import { cancelBooking, cancelBookingSubscription } from '../requests/booking-requests';
import {getAgreementUrl, startDateLessThan24Hours} from '../../util/BookingUtil';
import { useNavigate } from 'react-router';
import moment from 'moment';

export const modalTypes = {complete: "COMPLETE", cancel_request: 'CANCEL', payment: "PAYMENT", review: "REVIEW", termsOnly: "TERMS_ONLY", activeBookingCancel: 'CANCEL_ACTIVE', endMonthly: 'END_MONTHLY', updatePayment: 'UPDATE_PAYMENT'};

const BuyerBooking = (props: { account: Account, snackbarShowMessage: () => void, readPendingBooking: () => {} }) => {
  const {account, snackbarShowMessage, readPendingBooking} = props;

  const urlParams = useUrlQueryParams();
  const urlBookingNumber = urlParams.bookingNumber || "";
  const urlStatus = urlParams.status || "";

  const navigate = useNavigate();

  const getDefaultTab = useCallback((status) => {
    return BuyerBookingTabs[status?.toUpperCase()] || BuyerBookingTabs.ACTIVE;
  }, []);

  const defaultAdjustBooking = {open: false, booking: null};
    
  const [currentTab, setCurrentTab] = useState(getDefaultTab());
  const [activeBookings: Booking[], setActiveBookings] = useState([])
  const [inactiveBookings: Booking[], setInactiveBookings] = useState([]);
  const [failedBookings: Booking[], setFailedBookings] = useState([]);
  const [pendingBookings: Booking[], setPendingBookings] = useState([]);
  const [activePage, setActivePage] = useState(0);
  const [failedPage, setFailedPage] = useState(0);
  const [inactivePage, setInactivePage] = useState(0);
  const [pendingPage, setPendingPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [activeBookingCount, setActiveBookingCount] = useState(0);
  const [failedBookingCount, setFailedBookingCount] = useState(0);
  const [inactiveBookingCount, setInactiveBookingCount] = useState(0);
  const [pendingBookingCount, setPendingBookingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({
    supplierAccountId: account?.id,
    startDate: '',
    endDate: '',
    buyerName: '',
    bookingNumber: urlBookingNumber,
    sortBy: '',
    equipmentType: '',
    brokered: false,
    frequency: '',
    sortDir: ''
  });
  const [bookingToEdit, setBookingToEdit] = useState(defaultAdjustBooking);
  const [showModal, setShowModal] = useState({ show: false, type: modalTypes.payment })

  const userIsAdmin = hasAdminAccess(account);

  const classes = useStyles();

  const handleTabChange = (_event, value) => {
    setCurrentTab(value || BuyerBookingTabs.ACTIVE);
  };

  const onCloseDialog = () => {
    setBookingToEdit(defaultAdjustBooking)
  }

  const handleCloseModals = () => {
    setShowModal(prev => ({...prev, show: false}));
    onCloseDialog();
  };

  const onDialogSuccess = () => {
    readPendingBooking();
    handleCloseModals();
    loadBookings(query, BuyerBookingTabs.ACTIVE, setActiveBookings, setActiveBookingCount, activePage, rowsPerPage);
    loadBookings(query, BuyerBookingTabs.INACTIVE, setInactiveBookings, setInactiveBookingCount, inactivePage, rowsPerPage);
    loadBookings(query, BuyerBookingTabs.FAILED, setFailedBookings, setFailedBookingCount, failedPage, rowsPerPage);
    loadBookings(query, BuyerBookingTabs.PENDING, setPendingBookings, setPendingBookingCount, pendingPage, rowsPerPage);
  }

  const handleAdjustSpaces = (spaces) => {
    const {booking} = bookingToEdit;

    let newBooking = {...booking, numberOfSpaces: spaces}

    setBookingToEdit(prev => ({...prev, booking: newBooking}))
  }

  const handleAdjustEndDate = (date) => {
    const {booking} = bookingToEdit;

    let newBooking = {...booking, endDate: date}

    setBookingToEdit(prev => ({...prev, booking: newBooking}));
  }

  /**
   * Handle adjusting booking fields
   * @param event where the target name is the booking field to adjust
   */
  const handleAdjustBooking = (event) => {
    const {booking} = bookingToEdit;
    const {name, value} = event.target;
    let newBooking = {...booking };

    if (name === 'brokeredBuyerChargedPerOccurrence' || name === 'brokeredSupplierPaidPerOccurrence') {
      newBooking[name] = parseCurrencyValue(value);
    } else {
      newBooking[name] = value;
    }
    setBookingToEdit(prev => ({...prev, booking: newBooking}));
  }

  const handleEndBooking = (booking: Booking) => {
    if (booking.bookingSubscriptionStatus === "ACTIVE" &&
      BookingStatusesEligibleForCancellation.includes(booking.status)) {
      setBookingToEdit(booking);
        setShowModal({show: true, type: modalTypes.endMonthly});
    } else if (booking.status === BookingStatus.PENDING || ((booking.status === BookingStatus.APPROVED || booking.status === BookingStatus.PROCESSING_ACH_PAYMENT) && booking.frequency !== "RECURRING")) {
      if (!startDateLessThan24Hours(booking)) {
        setBookingToEdit(booking);
        setShowCancelBookingConfirmation(true);
        setShowModal({show: true, type: modalTypes.activeBookingCancel});
      } else {
        snackbarShowMessage("This booking is not eligible for cancellation because it is less than 24 hours before the booking start date.", "warning");
      }
    } else {
      // Remove this when we can programmatically disable the end bookings button
      snackbarShowMessage("This booking is not eligible for cancellation.", "warning");
    }
  };

  const handleUpdateBooking = (booking: Booking) => {
    if (BookingStatusesEligibleForPaymentMethodUpdate.includes(booking.status)) {
      setBookingToEdit(booking);
      setShowModal({show: true, type: modalTypes.updatePayment});
    } else {
      // Remove this when we can programmatically disable the update payment method button
      snackbarShowMessage("This booking is not eligible for payment method update.", "warning");
    }
  };

  const handleCancelPendingBooking = () => {
    Busy.set(true);
    cancelBooking({id: bookingToEdit.id}).then((response) => {
      const cancelledBooking = response.body;
      snackbarShowMessage(`Successfully cancelled booking ${cancelledBooking.orderNumber}`, "success");
      onDialogSuccess();
    }).catch((error) => {
      snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), "error");
    }).finally(() => {
      Busy.set(false);
    });
  };

  const handleMonthlyBookingCancellation = () => {
    Busy.set(true);
    cancelBookingSubscription({id: bookingToEdit.id}).then((response) => {
      const canceledBooking = response.body;
      snackbarShowMessage(`Subscription for booking ${canceledBooking.orderNumber} will end on ${moment(canceledBooking.endDate).format(DateFormats.DAY)}`, "success");
      onDialogSuccess();
    }).catch((error) => {
      snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), "error");
    }).finally(() => {
      Busy.set(false);
    });
  };

  const handleMakePayment = (booking: Booking) => {
    navigate(`/bookings/${booking.id}/failedPayment`);
  };

  const shouldShowTermsOnlyModal = (booking) => {
    return booking.manualPayment ||
      (booking.brokeredBuyerChargedPerOccurrence === 0 &&
      booking.brokeredSupplierPaidPerOccurrence === 0 &&
      booking.chargeOverages === false);
  }

  const handleShowPaymentModal = (booking) => {
    setBookingToEdit(booking);
    if (shouldShowTermsOnlyModal(booking)) {
      setShowModal({ show: true, type: modalTypes.termsOnly })
    } else {
      setShowModal({ show: true, type: modalTypes.payment });
    }
  }

  const handleShowCancelBookingRequestModal = (booking) => {
    setBookingToEdit(booking)
    setShowModal({ show: true, type: modalTypes.cancel_request })
  }

  const loadBookings = useCallback((
    query,
    status,
    setBookings,
    setBookingCount,
    page,
    pageSize
  ) => {
    const {id} = account;

    if (id) {
      setLoading(true);
      const requestParams = {
        accountId: account.id,
        locationId: query.selectedLocation?.id || '',
        status: status,
        page: page,
        size: pageSize,
        ...query,
      };

      getBuyerBookings(requestParams)
        .then(({body}) => {
          const {count} = body;
          setBookingCount(count);
          const data = body.content?.map((booking) => {
            return transformBooking(booking);
          });
          setBookings(data);
        })
        .catch(err => {
          snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), "error", 15000)
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [account, snackbarShowMessage]);

  useEffect(() => {
    loadBookings(query, BuyerBookingTabs.ACTIVE, setActiveBookings, setActiveBookingCount, activePage, rowsPerPage);
  }, [loadBookings, query, rowsPerPage, activePage]);

  useEffect(() => {
    loadBookings(query, BuyerBookingTabs.INACTIVE, setInactiveBookings, setInactiveBookingCount, inactivePage, rowsPerPage);
  }, [loadBookings, query, rowsPerPage, inactivePage]);

  useEffect(() => {
    loadBookings(query, BuyerBookingTabs.FAILED, setFailedBookings, setFailedBookingCount, failedPage, rowsPerPage);
  }, [loadBookings, query, rowsPerPage, failedPage]);

  useEffect(() => {
    loadBookings(query, BuyerBookingTabs.PENDING, setPendingBookings, setPendingBookingCount, pendingPage, rowsPerPage);
  }, [loadBookings, query, rowsPerPage, pendingPage]);

  useEffect(() => {
    if (urlStatus) {
      setCurrentTab(getDefaultTab(urlStatus))
    } else if (urlBookingNumber) {
      if (failedBookings.length) {
        setCurrentTab(BuyerBookingTabs.FAILED)
      } else if (inactiveBookings.length) {
        setCurrentTab(BuyerBookingTabs.INACTIVE)
      } else if (pendingBookings.length) {
        setCurrentTab(BuyerBookingTabs.PENDING)
      } else { //default to active
        setCurrentTab(BuyerBookingTabs.ACTIVE)
      }
    }
  }, [urlBookingNumber, activeBookings, inactiveBookings, failedBookings, pendingBookings, getDefaultTab, urlStatus]);

  return (
    <Container className={classes.mainContainer}>
      <Grid item><Typography variant={'h5'} component={'h1'}>Bookings</Typography></Grid>
      <Grid item className={classes.filterContainer}><BuyerBookingFilter setQuery={setQuery}/></Grid>
      <Grid item>
        <TabContext value={currentTab}>
          <Grid container justifyContent={'space-between'} alignItems={'center'}>
            <Grid item xs>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                aria-label={'Bookings Tabs'}
                textColor={'secondary'}
                variant={'fullWidth'}
              >
                <Tab value={BuyerBookingTabs.ACTIVE} label={formatTabHeader(BuyerBookingTabs.ACTIVE, activeBookingCount)}/>
                <Tab value={BuyerBookingTabs.PENDING} label={formatTabHeader(BuyerBookingTabs.PENDING, pendingBookingCount)}/>
                <Tab value={BuyerBookingTabs.INACTIVE} label={formatTabHeader(BuyerBookingTabs.INACTIVE, inactiveBookingCount)}/>
                <Tab value={BuyerBookingTabs.FAILED} label={formatTabHeader(BuyerBookingTabs.FAILED, failedBookingCount)}/>
              </Tabs>
            </Grid>
          </Grid>

          <TabPanel value={BuyerBookingTabs.ACTIVE} className={classes.tabPanel}>
            <BookingTab
              bookings={activeBookings}
              loading={loading}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              page={activePage}
              setPage={setActivePage}
              count={activeBookingCount}
              status={BuyerBookingTabs.ACTIVE}
              userIsAdmin={userIsAdmin}
              handleSelectAdjustBooking={handleUpdateBooking}
              handleEndBooking={handleEndBooking}
              accountType={AccountType.BUYER}
            />
          </TabPanel>
          <TabPanel value={BuyerBookingTabs.PENDING} className={classes.tabPanel}>
            <BookingTab
              bookings={pendingBookings}
              loading={loading}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              page={pendingPage}
              setPage={setPendingPage}
              count={pendingBookingCount}
              status={BuyerBookingTabs.PENDING}
              userIsAdmin={userIsAdmin}
              handleSelectAdjustBooking={handleUpdateBooking}
              handleShowPaymentModal={handleShowPaymentModal}
              handleShowCancelBookingRequestModal={handleShowCancelBookingRequestModal}
              accountType={AccountType.BUYER}
            />
          </TabPanel>
          <TabPanel value={BuyerBookingTabs.INACTIVE} className={classes.tabPanel}>
            <BookingTab
              bookings={inactiveBookings}
              loading={loading}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              page={inactivePage}
              setPage={setInactivePage}
              count={inactiveBookingCount}
              status={BuyerBookingTabs.INACTIVE}
              userIsAdmin={userIsAdmin}
              handleSelectAdjustBooking={handleUpdateBooking}
              accountType={AccountType.BUYER}
            />
          </TabPanel>
          <TabPanel value={BuyerBookingTabs.FAILED} className={classes.tabPanel}>
            <BookingTab
              bookings={failedBookings}
              loading={loading}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              page={failedPage}
              setPage={setFailedPage}
              count={failedBookingCount}
              status={BuyerBookingTabs.FAILED}
              userIsAdmin={userIsAdmin}
              handleSelectAdjustBooking={handleUpdateBooking}
              handleMakePayment={handleMakePayment}
              accountType={AccountType.BUYER}
            />
          </TabPanel>
        </TabContext>
      </Grid>
      <AdjustBookingDialog
        open={bookingToEdit.open}
        onConfirm={onDialogSuccess}
        onClose={onCloseDialog}
        type={bookingToEdit.type}
        booking={bookingToEdit.booking}
        handleAdjustEndDate={handleAdjustEndDate}
        handleAdjustSpaces={handleAdjustSpaces}
        handleAdjustBooking={handleAdjustBooking}
      />
      <BuyerActiveDialogs 
        account={account}
        bookingToEdit={bookingToEdit}
        showModal={showModal}
        handleCloseModals={handleCloseModals}
        handleBookingCancellation={handleMonthlyBookingCancellation} 
        handleMonthlyBookingCancellation={handleMonthlyBookingCancellation}
        handleUpdatePaymentMethod={onDialogSuccess}
      />
      <BuyerPendingDialogs
        account={account}
        bookingBeingActioned={bookingToEdit}
        showModal={showModal}
        setShowModal={setShowModal}
        handleCloseModals={handleCloseModals}
        getAgreementUrl={getAgreementUrl}
        onDialogSuccess={onDialogSuccess}
        handleCancelBooking={handleCancelPendingBooking}
        classes={classes}
      />
    </Container>
  );
};

export default withSnackbar(BuyerBooking);
